<script>
import TagsList from "@/system/components/global/tags/TagsList";

export default {
    components: {
        TagsList,
    },

    props: ['eventGroup', 'fullDescription', 'is_waiting_list_enabled'],

    computed: {
        eventDescription () {
            if (this.fullDescription) {
                return this.eventGroup.description_formatted
            } else {
                return this.$filters.truncate(this.eventGroup.description_formatted, 150)
            }
        },

        ends_on_different_year() {
            return moment(this.eventGroup.end).year() !== moment(this.eventGroup.start).year();
        },

        firstEvent() {
            return this.eventGroup.first_event;
        },

        canJoinEvent() {
            return !this.eventGroup.client_is_subscribed
                && !this.eventGroup.is_filled
                && this.eventGroup.is_open
                && !this.fullDescription;
        },

        canJoinWaitingList() {
            return this.eventGroup.waiting_list_enabled
                && this.is_waiting_list_enabled
                && this.eventGroup.is_filled
                && this.eventGroup.is_open
                && !this.eventGroup.client_is_subscribed
                && !this.fullDescription;
        },

        isMultidayEvent() {
            const start = moment(this.eventGroup.start);
            const end = moment(this.eventGroup.end);
            return !start.isSame(end, 'day');
        },

        canBook() {
            if (this.canJoinEvent || this.canJoinWaitingList) {
                return true;
            }
            return false;
        },

        hideTimes () {
            return this.isMultidayEvent && !this.eventGroup.has_same_start_and_end_time;
        },
    }
}
</script>


<template>

    <router-link :is="canBook ? 'router-link' : 'div'" :to="'/event/' + eventGroup.slug" class="event-card-wrapper">
        <div class="grey-text event-card h-full" :class="{'no-spaces': eventGroup.available_spots === 0 || !eventGroup.is_open}">

            <div class="event-attending" v-if="eventGroup.client_is_subscribed">
                <i class="zmdi zmdi-check"></i>
                {{ trans('events.already_subscribed_label') }}
            </div>

            <div class="event-attending" v-if="eventGroup.client_is_waiting_list">
                <i class="zmdi zmdi-check"></i>
                {{ trans('events.already_waiting_list_label') }}
            </div>

            <div
                class="events-img"
                :class="eventGroup.has_image ? 'has-image' : 'p-0'"
                :style="{backgroundImage: 'url(' + eventGroup.image_url + ')'}"
            >
            </div>

            <div class="events-content-container">
                <div class="flex flex-col justify-between flex-1 p-6">
                    <div class="event-content">
                        <div class="event-date gap-6">

                            <div class="event-dates">
                                <div class="event-day-month n-color-text-weak">
                                    <div class="event-day">{{ $filters.humanize_day(eventGroup.start)}}</div>
                                    <div class="event-month">{{ $filters.humanize_month_short(eventGroup.start) }}</div>
                                </div>

                                <span v-if="isMultidayEvent" class="event-date-spacer n-color-text-weak">—</span>

                                <div class="event-day-month n-color-text-weak" v-if="isMultidayEvent">
                                    <div class="event-day">{{ $filters.humanize_day(eventGroup.end)}}</div>
                                    <div class="event-month">
                                        {{ $filters.humanize_month_short(eventGroup.end) }}
                                        <span v-if="ends_on_different_year">{{ $filters.capitalize($filters.humanize_year_short(eventGroup.end)) }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="event-times n-color-text-weak leading-relaxed">
                                <span :class="{'block': !isMultidayEvent}">{{ $filters.humanize_day_of_week(eventGroup.start) }}</span>

                                <span class="event-time" v-show="!hideTimes">{{ $filters.lowercase($filters.humanize_time(eventGroup.start)) }}</span>

                                <span v-if="!isMultidayEvent">
                                    - <span class="event-time">{{ $filters.lowercase($filters.humanize_time(eventGroup.end)) }}</span>
                                </span>

                                <span class="event-end" v-else>
                                    <span>{{ $filters.humanize_day_of_week(eventGroup.end) }}</span>
                                    <span class="event-time" v-show="!hideTimes">{{ $filters.lowercase($filters.humanize_time(eventGroup.end)) }} </span>
                                </span>
                            </div>

                        </div>

                        <h3 class="event-title n-color-text" >{{ eventGroup.name }}</h3>

                        <div class="event-course n-color-text">
                            <b v-if="eventGroup.events_count > 0">
                                {{ $filters.capitalize(trans('event_groups.hardcoded_label')) }} -
                                <span>{{ eventGroup.events_count }}</span>
                                {{ $filters.lowercase(trans('event_groups.sessions')) }}
                                <span v-if="firstEvent && firstEvent.practitioners && firstEvent.practitioners.length > 0"> - </span>
                            </b>
                            <span v-if="firstEvent && firstEvent.practitioners">
                            <b v-for="practitioner in firstEvent.practitioners" :key="practitioner.id">{{ practitioner.realname }}</b>
                        </span>
                        </div>
                        <p class="mb-0 n-color-text-weak" v-if="eventGroup.description_formatted" v-sanitize-html="eventDescription"></p>

                        <tags-list :tags="eventGroup.tags" theme="client-universe" class="event-tags mb-5" />
                    </div>

                    <button v-if="canJoinEvent" class="btn btn-custom btn-lg rounded-md mt-6">
                        {{ trans('online_booking.join_event') }}
                    </button>
                    <button v-else-if="canJoinWaitingList" class="btn btn-trans text-brand-primary btn-lg font-bold rounded-md mt-6">
                        {{ trans('waiting_list.event_join') }}
                    </button>
                    <button v-else-if="!fullDescription" class="btn btn-trans btn-lg font-bold rounded-md mt-6 n-color-text" disabled>
                        {{ eventGroup.is_filled ? trans('online_booking.fully_booked') : trans('online_booking.event_closed') }}
                    </button>
                </div>
                <div class="event-footer">
                    <div class="event-price n-color-text" v-if="eventGroup.price_formatted_with_vat">
                        {{ eventGroup.price_formatted_with_vat }}
                    </div>
                    <div class="event-spots n-color-text" v-if="!eventGroup.hide_spots && eventGroup.is_open">
                        <span v-if="eventGroup.available_spots == 1">
                            <span>{{ eventGroup.available_spots }}</span>
                            {{ trans('events.spot_left') }}
                        </span>
                        <span v-else>{{ eventGroup.available_spots }} {{ trans('events.spots_left') }}</span>
                    </div>
                    <div class="event-address n-color-text" v-if="eventGroup.events_count > 0 && firstEvent && (firstEvent.address || firstEvent.city || firstEvent.zip)">
                        <a target="_blank" :href="'http://maps.google.com/?q=' + firstEvent.address + ',' + firstEvent.zip + ',' + firstEvent.city" style="color: #797979;">
                            <span v-if="firstEvent.address">{{ firstEvent.address }}<span v-if="firstEvent.city || firstEvent.zip">, </span></span>
                            <span v-if="firstEvent.zip">{{ firstEvent.zip }}<span v-if="firstEvent.city">, </span></span>
                            <span v-if="firstEvent.city">{{ firstEvent.city }}</span>
                        </a>
                    </div>
                </div>

            </div>

        </div>
    </router-link>
</template>
